<template>
  <div>
    <div class="filter-grid">
      <!-- Tag Filter -->
      <div class="filter-item width-2">
        <h4 class="mb-2">{{ i18n.t('tags') }}</h4>
        <SmSelect
          v-model:selection="selectedTags"
          :options="tagOptions"
          :label="i18n.t('chooseTags')"
          :multiselect="true"
          searchable />
      </div>

      <!-- Source Filter -->
      <div class="filter-item width-2">
        <h4 class="mb-2">{{ i18n.t('dataSources') }}</h4>
        <SmSelect
          v-model:selection="selectedSources"
          :options="sourceOptions"
          :multiselect="true"
          :label="i18n.t('chooseDataSources')">
          <template #item="{ item }">
            <div class="flex items-center gap-3">
              <TextMultiline v-if="item.key === 'all'">
                {{ item.label }}
              </TextMultiline>
              <AccountStatusSmall v-else :status="item.value" class="mb-1" />
            </div>
          </template>
        </SmSelect>
      </div>

      <!-- General Filters -->
      <div class="filter-item width-2">
        <div class="mb-2 flex items-center justify-between">
          <span>{{ i18n.t('hideWithoutCosts') }}</span>
          <SmSwitch
            v-model="filter.hideWithoutCosts"
            style="font-size: 0.75rem" />
        </div>
        <div class="mb-2 flex items-center justify-between">
          <span>{{ i18n.t('hideWithCosts') }}</span>
          <SmSwitch v-model="filter.hideWithCosts" style="font-size: 0.75rem" />
        </div>
        <div class="mb-2 flex items-center justify-between">
          <span>{{ i18n.t('hideWithoutUser') }}</span>
          <SmSwitch
            v-model="filter.hideWithoutAccount"
            style="font-size: 0.75rem" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { PaginationSortOrder, TagsService } from '@/client'
  import { Tag } from '@/client/models/Tag'
  import { useI18n } from 'vue-i18n'
  import { Option } from '@/components/sm/SmSelect.vue'
  import {
    ApplicationFilter,
    ApplicationSourceFilter,
  } from '@/stores/application/application.filter'
  import { CustomStatus } from '@/components/accounts/AccountStatusSmall.vue'

  const i18n = useI18n()

  const filter = defineModel<ApplicationFilter>('filter', {
    required: true,
  })

  // ####################################
  // Tags
  // ####################################

  const selectedTags = ref<Option<Tag>[]>([])
  const tagOptions = ref<Option<Tag>[]>([])

  async function loadTags() {
    await TagsService.getTagsApiV1ManagementTagsGet({
      sortBy: 'sorting',
      sortOrder: PaginationSortOrder.DESC,
    }).then((tags) => {
      tagOptions.value = tags.map((tag) => {
        return {
          label: tag.name,
          value: tag,
        }
      })
    })
  }

  watch(
    selectedTags,
    () => {
      filter.value.tags =
        selectedTags.value && selectedTags.value.length
          ? selectedTags.value.map((tag) => tag.value._id)
          : undefined
    },
    { deep: true }
  )

  onMounted(async () => {
    await loadTags()

    selectedTags.value = filter.value.tags
      ? tagOptions.value.filter((tag) => {
          return filter.value.tags?.includes(tag.value._id)
        })
      : []
  })

  // ####################################
  // Data Sources
  // ####################################

  const selectedSources = ref<Option<CustomStatus>[]>([])
  const sourceOptions = ref<Option<CustomStatus>[]>([
    {
      key: ApplicationSourceFilter.MANUALLY,
      label: i18n.t('general.applicationTypeTag.manually'),
      value: {
        text: i18n.t('general.applicationTypeTag.manually'),
        color: 'var(--sm-contrast-muted)',
      },
    },
    {
      key: ApplicationSourceFilter.SSO,
      label: i18n.t('general.applicationTypeTag.sso'),
      value: {
        text: i18n.t('general.applicationTypeTag.sso'),
        color: 'var(--sm-blue)',
      },
    },
    {
      key: ApplicationSourceFilter.INTEGRATION,
      label: i18n.t('general.applicationTypeTag.integration'),
      value: {
        text: i18n.t('general.applicationTypeTag.integration'),
        color: 'var(--sm-green)',
      },
    },
  ])

  watch(
    selectedSources,
    () => {
      filter.value.isSource = selectedSources.value
        ? (selectedSources.value.map(
            (source) => source.key
          ) as ApplicationSourceFilter[])
        : []
    },
    { deep: true }
  )

  onMounted(() => {
    selectedSources.value = filter.value.isSource
      ? sourceOptions.value.filter((option) =>
          filter.value.isSource?.includes(option.key as ApplicationSourceFilter)
        )
      : []
  })
</script>

<style lang="scss" scoped>
  .filter-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 1rem;
  }

  .filter-item {
    display: flex;
    flex-direction: column;
  }

  .width-2 {
    grid-column: span 2;
  }
</style>
style
